import * as React from 'react';
import {
    Card, CardContent, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    CircularProgress, Alert, IconButton, Menu, MenuItem, FormControlLabel,
    Checkbox, Tooltip, Badge, TextField, InputAdornment, Switch,
    FormControl, InputLabel, Select, Divider, Avatar
} from '@mui/material';
import { useNotify } from 'react-admin';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Configurazione delle colonne (uguale alla dashboard ma estesa)
const COLUMN_CONFIG = {
    posizione: {
        label: 'Pos.',
        key: 'posizione',
        align: 'left',
        default: true,
        sortable: true,
        width: 80,
        render: (value, row, index) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {(value || index + 1) === 1 && (
                    <EmojiEvents sx={{ color: '#FFD700', fontSize: 28 }} />
                )}
                {(value || index + 1) === 2 && (
                    <EmojiEvents sx={{ color: '#C0C0C0', fontSize: 26 }} />
                )}
                {(value || index + 1) === 3 && (
                    <EmojiEvents sx={{ color: '#CD7F32', fontSize: 24 }} />
                )}
                <Chip 
                    label={value || index + 1} 
                    size="small" 
                    color={(value || index + 1) <= 3 ? 'success' : 'default'}
                    sx={{ fontWeight: 700 }}
                />
            </Box>
        )
    },
    squadra: {
        label: 'Squadra',
        key: 'squadra',
        align: 'left',
        default: true,
        sortable: true,
        width: 250,
        render: (value, row, index, teamsData) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                    src={teamsData?.get(row.teamId)?.foto ? `${host}/image-proxy-mw?url=${encodeURIComponent(teamsData.get(row.teamId).foto)}` : undefined}
                    alt={value}
                    sx={{ width: 32, height: 32 }}
                >
                    {value?.charAt(0).toUpperCase() || '⚽'}
                </Avatar>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: index < 3 ? 600 : 400,
                        cursor: 'pointer',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                    component="a"
                    href={`#/squadre/${row.teamId}`}
                >
                    {value || `Squadra ${index + 1}`}
                </Typography>
            </Box>
        )
    },
    punti: {
        label: 'Punti',
        key: 'punti',
        align: 'center',
        default: true,
        sortable: true,
        width: 80,
        render: (value, row, index) => (
            <Chip
                label={value || 0}
                size="small"
                sx={{
                    bgcolor: index < 3 ? getPositionColor(index) : '#e0e0e0',
                    color: index < 3 ? '#fff' : '#666',
                    fontWeight: 600
                }}
            />
        )
    },
    partiteGiocate: {
        label: 'P.G.',
        key: 'partiteGiocate',
        align: 'center',
        default: true,
        sortable: true,
        width: 60,
        render: (value) => <Typography variant="body2">{value || 0}</Typography>
    },
    vinte: {
        label: 'V',
        key: 'vinte',
        align: 'center',
        default: true,
        sortable: true,
        width: 50,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                {value || 0}
            </Typography>
        )
    },
    pareggiate: {
        label: 'P',
        key: 'pareggiate',
        align: 'center',
        default: true,
        sortable: true,
        width: 50,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 600 }}>
                {value || 0}
            </Typography>
        )
    },
    perse: {
        label: 'S',
        key: 'perse',
        align: 'center',
        default: true,
        sortable: true,
        width: 50,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600 }}>
                {value || 0}
            </Typography>
        )
    },
    goalFatti: {
        label: 'GF',
        key: 'goalFatti',
        align: 'center',
        default: true,
        sortable: true,
        width: 60,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 500 }}>
                {value || 0}
            </Typography>
        )
    },
    goalSubiti: {
        label: 'GS',
        key: 'goalSubiti',
        align: 'center',
        default: true,
        sortable: true,
        width: 60,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 500 }}>
                {value || 0}
            </Typography>
        )
    },
    differenzaReti: {
        label: 'DR',
        key: 'differenzaReti',
        align: 'center',
        default: true,
        sortable: true,
        width: 80,
        render: (value) => {
            const diff = value || 0;
            const color = diff > 0 ? '#4caf50' : diff < 0 ? '#f44336' : '#757575';
            const icon = diff > 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> :
                diff < 0 ? <TrendingDownIcon sx={{ fontSize: 14 }} /> :
                    <RemoveIcon sx={{ fontSize: 14 }} />;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
                        {diff > 0 ? `+${diff}` : diff}
                    </Typography>
                    {React.cloneElement(icon, { sx: { ...icon.props.sx, color } })}
                </Box>
            );
        }
    },
    ultime5: {
        label: 'Ultime 5',
        key: 'ultime5',
        align: 'center',
        default: false,
        sortable: false,
        width: 120,
        render: (value) => {
            if (!value) return <Typography variant="body2">-</Typography>;
            return (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {value.split('').map((result, idx) => {
                        const color = result === 'V' ? '#4caf50' :
                            result === 'P' ? '#ff9800' :
                                result === 'S' ? '#f44336' : '#e0e0e0';
                        return (
                            <Chip
                                key={idx}
                                label={result}
                                size="small"
                                sx={{
                                    bgcolor: color,
                                    color: '#fff',
                                    fontSize: '0.6rem',
                                    height: 22,
                                    width: 22,
                                    minWidth: 22
                                }}
                            />
                        );
                    })}
                </Box>
            );
        }
    },
    percentualeVittorie: {
        label: '% Vitt.',
        key: 'percentualeVittorie',
        align: 'center',
        default: false,
        sortable: true,
        width: 80,
        render: (value, row) => {
            const partite = row.partiteGiocate || 0;
            const vittorie = row.vinte || 0;
            const percentuale = partite > 0 ? ((vittorie / partite) * 100).toFixed(1) : 0;
            return (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {percentuale}%
                </Typography>
            );
        }
    }
};

// Funzioni helper
const getPositionColor = (index) => {
    if (index === 0) return '#FFD700'; // Oro
    if (index === 1) return '#C0C0C0'; // Argento
    if (index === 2) return '#CD7F32'; // Bronzo
    return '#757575'; // Grigio per le altre posizioni
};

const getPositionIcon = (index) => {
    if (index < 3) {
        return <EmojiEventsIcon sx={{ color: getPositionColor(index), fontSize: 20 }} />;
    }
    return null;
};

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

function ClassificaPage() {
    const notify = useNotify();
    const [classifica, setClassifica] = React.useState([]);
    const [classificaFiltered, setClassificaFiltered] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [fullscreen, setFullscreen] = React.useState(false);
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [refreshInterval, setRefreshInterval] = React.useState(null);
    
    // Filtri per lega e stagione
    const [leghe, setLeghe] = React.useState([]);
    const [stagioni, setStagioni] = React.useState([]);
    const [filterLega, setFilterLega] = React.useState('all');
    const [filterStagione, setFilterStagione] = React.useState('all');
    const [teamsData, setTeamsData] = React.useState(new Map());

    const [visibleColumns, setVisibleColumns] = React.useState(() => {
        const saved = localStorage.getItem('classifica-visible-columns');
        if (saved) {
            return JSON.parse(saved);
        }
        return Object.keys(COLUMN_CONFIG).filter(key => COLUMN_CONFIG[key].default);
    });

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [sortConfig, setSortConfig] = React.useState({ key: 'punti', direction: 'desc' });

    // Carica leghe e stagioni
    const loadFilters = async () => {
        try {
            const jwt = getJwt();
            const [legheRes, stagioniRes] = await Promise.all([
                axios.get(`${host}/tbl-lega`, { 
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: { $limit: 1000, $sort: { nome: 1 } }
                }),
                axios.get(`${host}/tbl-stagione`, { 
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: { $limit: 1000, $sort: { id: -1 } }
                })
            ]);
            setLeghe(legheRes.data.data || []);
            setStagioni(stagioniRes.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento filtri:", error);
        }
    };

    // Carica la classifica
    const loadClassifica = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const params = {
                $limit: 1000,
                $sort: { stagioneId: -1, posizione: 1 }
            };
            
            if (filterLega !== 'all') {
                params.legaId = Number(filterLega);
            }
            if (filterStagione !== 'all') {
                params.stagioneId = Number(filterStagione);
            }

            const response = await axios.get(
                `${host}/tbl-classifica-snapshot-generale`,
                { 
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params
                }
            );

            let classificaData = response.data.data || [];

            // Applica ordinamento
            if (sortConfig.key && COLUMN_CONFIG[sortConfig.key]?.sortable) {
                classificaData = [...classificaData].sort((a, b) => {
                    const aVal = a[sortConfig.key] || 0;
                    const bVal = b[sortConfig.key] || 0;

                    if (sortConfig.direction === 'asc') {
                        return aVal - bVal;
                    } else {
                        return bVal - aVal;
                    }
                });
            }

            setClassifica(classificaData);
            setClassificaFiltered(classificaData);

            // Carica dati team per stemmi
            const uniqueTeamIds = [...new Set(classificaData.map(c => c.teamId))];
            if (uniqueTeamIds.length > 0) {
                const teamsRes = await axios.get(`${host}/tbl-team`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        id: { $in: uniqueTeamIds },
                        $limit: uniqueTeamIds.length
                    }
                });
                const teamsMap = new Map();
                (teamsRes.data.data || []).forEach(team => {
                    teamsMap.set(team.id, team);
                });
                setTeamsData(teamsMap);
            }
        } catch (error) {
            console.error("Errore nel recupero della classifica:", error);
            setError("Errore nel caricamento della classifica");
            notify("Errore nel caricamento della classifica", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Filtro ricerca
    React.useEffect(() => {
        if (searchTerm.trim() === '') {
            setClassificaFiltered(classifica);
        } else {
            const filtered = classifica.filter(squadra =>
                squadra.squadra?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setClassificaFiltered(filtered);
        }
    }, [searchTerm, classifica]);

    // Auto refresh
    React.useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(() => {
                loadClassifica();
            }, 30000); // 30 secondi
            setRefreshInterval(interval);
        } else {
            if (refreshInterval) {
                clearInterval(refreshInterval);
                setRefreshInterval(null);
            }
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [autoRefresh]);

    // Carica i dati al mount del componente
    React.useEffect(() => {
        loadFilters();
    }, []);

    React.useEffect(() => {
        loadClassifica();
    }, [sortConfig, filterLega, filterStagione]);

    // Salva configurazione colonne
    React.useEffect(() => {
        localStorage.setItem('classifica-visible-columns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    const handleColumnToggle = (columnKey) => {
        setVisibleColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleSort = (columnKey) => {
        if (!COLUMN_CONFIG[columnKey]?.sortable) return;

        setSortConfig(prev => ({
            key: columnKey,
            direction: prev.key === columnKey && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const resetToDefault = () => {
        setVisibleColumns(Object.keys(COLUMN_CONFIG).filter(key => COLUMN_CONFIG[key].default));
        setAnchorEl(null);
    };

    const exportToCSV = () => {
        const headers = visibleColumns.map(key => COLUMN_CONFIG[key].label).join(',');
        const rows = classificaFiltered.map((squadra, index) =>
            visibleColumns.map(key => {
                const config = COLUMN_CONFIG[key];
                let value = squadra[config.key] || '';

                // Gestione valori speciali
                if (key === 'posizione') value = squadra.posizione || index + 1;
                if (key === 'percentualeVittorie') {
                    const partite = squadra.partiteGiocate || 0;
                    const vittorie = squadra.vinte || 0;
                    value = partite > 0 ? ((vittorie / partite) * 100).toFixed(1) : 0;
                }

                return `"${value}"`;
            }).join(',')
        ).join('\n');

        const csv = headers + '\n' + rows;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `classifica_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    return (
        <Box sx={{
            p: fullscreen ? 1 : { xs: 2, md: 4 },
            bgcolor: '#f6f7fb',
            minHeight: '100vh',
            position: fullscreen ? 'fixed' : 'relative',
            top: fullscreen ? 0 : 'auto',
            left: fullscreen ? 0 : 'auto',
            right: fullscreen ? 0 : 'auto',
            bottom: fullscreen ? 0 : 'auto',
            zIndex: fullscreen ? 9999 : 'auto'
        }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        🏆 Classifica Completa
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: 'grey.600' }}>
                        Visualizzazione dettagliata della classifica squadre con controlli avanzati
                    </Typography>
                </Box>

                {/* Controlli superiori */}
                <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Grid container spacing={2} alignItems="center">

                        {/* Ricerca */}
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Cerca squadra..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Filtro Lega */}
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Lega</InputLabel>
                                <Select
                                    value={filterLega}
                                    label="Lega"
                                    onChange={(e) => setFilterLega(e.target.value)}
                                >
                                    <MenuItem value="all">Tutte le leghe</MenuItem>
                                    {leghe.map(lega => (
                                        <MenuItem key={lega.id} value={String(lega.id)}>{lega.nome}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filtro Stagione */}
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stagione</InputLabel>
                                <Select
                                    value={filterStagione}
                                    label="Stagione"
                                    onChange={(e) => setFilterStagione(e.target.value)}
                                >
                                    <MenuItem value="all">Tutte le stagioni</MenuItem>
                                    {stagioni.map(stagione => (
                                        <MenuItem key={stagione.id} value={String(stagione.id)}>{stagione.nome || `Stagione ${stagione.id}`}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Auto refresh */}
                        <Grid item xs={12} md={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={autoRefresh}
                                        onChange={(e) => setAutoRefresh(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Auto refresh (30s)"
                                sx={{ margin: 0 }}
                            />
                        </Grid>

                        {/* Controlli */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>

                                <Tooltip title="Esporta CSV">
                                    <IconButton
                                        size="small"
                                        onClick={exportToCSV}
                                        disabled={!classificaFiltered.length}
                                    >
                                        <GetAppIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Configura colonne">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setAnchorEl(e.currentTarget)}
                                    >
                                        <Badge badgeContent={visibleColumns.length} color="primary">
                                            <SettingsIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={fullscreen ? "Esci da schermo intero" : "Schermo intero"}>
                                    <IconButton
                                        size="small"
                                        onClick={toggleFullscreen}
                                    >
                                        {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </IconButton>
                                </Tooltip>

                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                                    onClick={loadClassifica}
                                    disabled={loading}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Aggiorna
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                {/* Menu configurazione colonne */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                        sx: { maxHeight: 500, width: 280 }
                    }}
                >
                    <MenuItem onClick={resetToDefault}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Reset a default
                        </Typography>
                    </MenuItem>
                    <Divider />
                    {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                        <MenuItem key={key} onClick={() => handleColumnToggle(key)}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={visibleColumns.includes(key)}
                                        size="small"
                                    />
                                }
                                label={config.label}
                                sx={{ margin: 0, width: '100%' }}
                            />
                        </MenuItem>
                    ))}
                </Menu>

                {/* Statistiche rapide */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A90E2' }}>
                                {classificaFiltered.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Squadre{searchTerm ? ' trovate' : ''}
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {classificaFiltered.reduce((sum, squadra) => sum + (squadra.goalFatti || 0), 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Goal Totali
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                {Math.round(classificaFiltered.reduce((sum, squadra) => sum + (squadra.partiteGiocate || 0), 0) / (classificaFiltered.length || 1))}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Media Partite
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>
                                {classificaFiltered.length > 0 ? Math.max(...classificaFiltered.map(s => s.punti || 0)) : 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Punti Leader
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Tabella principale */}
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>

                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && !classificaFiltered.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : classificaFiltered.length > 0 ? (
                        <TableContainer sx={{ maxHeight: fullscreen ? 'calc(100vh - 300px)' : 700 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {visibleColumns.map(columnKey => {
                                            const config = COLUMN_CONFIG[columnKey];
                                            return (
                                                <TableCell
                                                    key={columnKey}
                                                    align={config.align}
                                                    sx={{
                                                        fontWeight: 600,
                                                        width: config.width,
                                                        cursor: config.sortable ? 'pointer' : 'default',
                                                        bgcolor: sortConfig.key === columnKey ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                        '&:hover': config.sortable ? {
                                                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                        } : {},
                                                        borderBottom: '2px solid #e0e0e0'
                                                    }}
                                                    onClick={() => handleSort(columnKey)}
                                                >
                                                    {config.label}
                                                    {sortConfig.key === columnKey && (
                                                        <span style={{ marginLeft: 4 }}>
                                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {classificaFiltered.map((squadra, index) => (
                                        <TableRow
                                            key={squadra.id || index}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                },
                                                bgcolor: index < 3 ? 'rgba(255, 215, 0, 0.1)' :
                                                    index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit'
                                            }}
                                        >
                                            {visibleColumns.map(columnKey => {
                                                const config = COLUMN_CONFIG[columnKey];
                                                const value = squadra[config.key];

                                                return (
                                                    <TableCell
                                                        key={columnKey}
                                                        align={config.align}
                                                        sx={{
                                                            width: config.width,
                                                            py: 1
                                                        }}
                                                    >
                                                        {config.render ?
                                                            config.render(value, squadra, index, teamsData) :
                                                            value || '-'
                                                        }
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 6 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {searchTerm ? 'Nessuna squadra trovata' : 'Nessuna squadra nella classifica'}
                            </Typography>
                            {searchTerm && (
                                <Button
                                    variant="outlined"
                                    onClick={() => setSearchTerm('')}
                                    sx={{ mt: 2 }}
                                >
                                    Cancella filtro
                                </Button>
                            )}
                        </Box>
                    )}
                </Card>

                {/* Footer con info */}
                {classificaFiltered.length > 0 && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                            {autoRefresh && " • Auto refresh attivo"}
                        </Typography>
                    </Box>
                )}

            </motion.div>
        </Box>
    );
}

export default ClassificaPage;